import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, DataTable, FilterBar, Modal, Drawer, Tabs, FormField, TextInput, StatusBadge, EmptyState, PageHeader, ConfirmDialog, Toggle } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';
import { Icon } from '@/components/ui/Icon';
import { useAuthStore, type AuthUser, type Persona } from '@/shared/stores/auth';

interface TenantRow {
  id: number;
  slug: string;
  name: string;
  status: 'active' | 'trial' | 'suspended' | string;
  max_agents: number;
  price_per_user_cents?: number;
  price_per_minute_cents?: number;
  locale: string;
  timezone?: string;
  currency?: string;
  plan_id?: number | null;
  trial_ends_at?: string | null;
  created_at?: string;
  agent_count?: number;
  sip_domain?: string | null;
}

interface TenantUser {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  phone?: string | null;
  extension?: string | null;
  two_factor_enabled?: boolean;
  last_login_at?: string | null;
  created_at?: string;
}

const USER_ROLES: { value: string; he: string }[] = [
  { value: 'owner', he: 'בעלים' },
  { value: 'admin', he: 'מנהל' },
  { value: 'supervisor', he: 'מפקח' },
  { value: 'qa', he: 'בקרת איכות' },
  { value: 'agent', he: 'נציג' },
  { value: 'viewer', he: 'צופה' },
];

const roleLabel = (role: string): string => USER_ROLES.find((r) => r.value === role)?.he ?? role;

interface InvoiceRow {
  id: number;
  number?: string;
  period?: string;
  amount_cents: number;
  currency: string;
  status: string;
  due_at?: string | null;
  paid_at?: string | null;
}

interface BillingPayload {
  plan: { id: number; code: string; name: string; monthly_price_cents: number; currency: string; minutes_included: number; max_agents: number } | null;
  invoices: InvoiceRow[];
  stats: { outstanding_invoices: number; total_invoices: number };
  trial_ends_at: string | null;
}

type StatusFilter = '' | 'active' | 'trial' | 'suspended';

const STATUS_LABEL: Record<string, string> = {
  active: 'פעיל',
  trial: 'ניסיון',
  suspended: 'מושעה',
};

function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (status === 'active') return 'ok';
  if (status === 'trial') return 'warn';
  if (status === 'suspended') return 'danger';
  return 'neutral';
}

export function Tenants() {
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TenantRow | null>(null);
  const [confirm, setConfirm] = useState<{ tenant: TenantRow; mode: 'suspend' | 'activate' } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantRow | null>(null);

  const [drawer, setDrawer] = useState<TenantRow | null>(null);
  const [drawerFull, setDrawerFull] = useState<TenantRow | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [tab, setTab] = useState<'overview' | 'users' | 'billing' | 'logs'>('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { per_page: 100 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const res = await superApi.tenants(params);
      setRows((res.data as TenantRow[]) ?? []);
    } catch (e) {
      toast('שגיאה בטעינת לקוחות', 'danger');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (!drawer) {
      setDrawerFull(null);
      return;
    }
    setDrawerLoading(true);
    setTab('overview');
    void superApi
      .tenant(drawer.id)
      .then((d) => setDrawerFull(d as TenantRow))
      .catch(() => toast('שגיאה בטעינת פרטי לקוח', 'danger'))
      .finally(() => setDrawerLoading(false));
  }, [drawer]);

  async function handleSuspend(tenant: TenantRow) {
    try {
      await superApi.suspendTenant(tenant.id);
      toast('הלקוח הושעה', 'ok');
      setConfirm(null);
      if (drawer?.id === tenant.id) setDrawer({ ...drawer, status: 'suspended' });
      await load();
    } catch {
      toast('שגיאה בהשעיית הלקוח', 'danger');
    }
  }

  async function handleActivate(tenant: TenantRow) {
    try {
      await superApi.activateTenant(tenant.id);
      toast('הלקוח הופעל', 'ok');
      setConfirm(null);
      if (drawer?.id === tenant.id) setDrawer({ ...drawer, status: 'active' });
      await load();
    } catch {
      toast('שגיאה בהפעלת הלקוח', 'danger');
    }
  }

  async function handleDelete(tenant: TenantRow, confirmText: string) {
    try {
      const res = await superApi.deleteTenant(tenant.id, confirmText);
      const rep = res.data;
      toast(`הלקוח נמחק לצמיתות${rep ? ` (${rep.tickets_preserved} טיקטים נשמרו)` : ''}`, 'ok');
      setDeleteTarget(null);
      if (drawer?.id === tenant.id) setDrawer(null);
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה במחיקת הלקוח', 'danger');
    }
  }

  const cols: DataTableColumn<TenantRow>[] = useMemo(
    () => [
      { key: 'name', he: 'שם' },
      { key: 'slug', he: 'מזהה', render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{r.slug}</span>
          {/* קישור פורטל ההקלטות של אותה מרכזייה — פר-שורה, כי הסלאג
              בנתיב הוא מה שתוחם את החיפוש לעסק אחד. */}
          <button type="button" className="btn ghost" title="העתק קישור לפורטל ההקלטות"
            style={{ padding: '0 4px', height: 22 }}
            onClick={(e) => {
              e.stopPropagation();
              const url = `${window.location.origin}/p/${r.slug}/recordings`;
              navigator.clipboard?.writeText(url).catch(() => window.prompt('העתק את הקישור:', url));
              toast('הקישור הועתק', 'ok');
            }}>
            <Icon name="copy" size={12} />
          </button>
        </span>
      ) },
      {
        key: 'agent_count',
        he: 'נציגים',
        align: 'end',
        render: (r) => `${r.agent_count ?? 0} / ${r.max_agents}`,
      },
      { key: 'locale', he: 'שפה', align: 'center' },
      {
        key: 'status',
        he: 'סטטוס',
        render: (r) => (
          <StatusBadge tone={statusTone(r.status)} dot size="sm">
            {STATUS_LABEL[r.status] ?? r.status}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        he: 'פעולות',
        align: 'end',
        render: (r) => (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn ghost" onClick={() => setEditTarget(r)}>
              ערוך
            </button>
            {r.status === 'suspended' ? (
              <button type="button" className="btn ok" onClick={() => setConfirm({ tenant: r, mode: 'activate' })}>
                הפעל
              </button>
            ) : (
              <button type="button" className="btn danger" onClick={() => setConfirm({ tenant: r, mode: 'suspend' })}>
                השעה
              </button>
            )}
            <button type="button" className="btn danger" onClick={() => setDeleteTarget(r)} title="מחיקה לצמיתות">
              מחק
            </button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="ניהול לקוחות" actions={<>
        <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
          + לקוח חדש
        </button>
      </>} />

      <FilterBar
        search={search}
        onSearch={setSearch}
        onClear={() => {
          setSearch('');
          setStatusFilter('');
        }}
        placeholder="חיפוש לפי שם או מזהה…"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="select"
          style={{ minWidth: 140 }}
        >
          <option value="">כל הסטטוסים</option>
          <option value="active">פעיל</option>
          <option value="trial">ניסיון</option>
          <option value="suspended">מושעה</option>
        </select>
      </FilterBar>

      <Card padded={false}>
        <DataTable<TenantRow>
          columns={cols}
          rows={rows}
          loading={loading}
          onRowClick={setDrawer}
          emptyState={<EmptyState icon="info" title="אין עדיין לקוחות" description="צור את הלקוח הראשון כדי להתחיל." action={<button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ לקוח חדש</button>} />}
        />
      </Card>

      {createOpen && (
        <TenantForm
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}

      {editTarget && (
        <TenantForm
          mode="edit"
          tenant={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void load();
          }}
        />
      )}

      {confirm && (
        <Modal
          isOpen
          onClose={() => setConfirm(null)}
          title={confirm.mode === 'suspend' ? 'השעיית לקוח' : 'הפעלת לקוח'}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(null)}>
                ביטול
              </button>
              {confirm.mode === 'suspend' ? (
                <button type="button" className="btn danger" onClick={() => handleSuspend(confirm.tenant)}>
                  השעה
                </button>
              ) : (
                <button type="button" className="btn primary" onClick={() => handleActivate(confirm.tenant)}>
                  הפעל
                </button>
              )}
            </>
          }
        >
          <p>
            {confirm.mode === 'suspend'
              ? `האם להשעות את ${confirm.tenant.name}? המשתמשים יאבדו גישה.`
              : `האם להפעיל מחדש את ${confirm.tenant.name}?`}
          </p>
        </Modal>
      )}

      {deleteTarget && (
        <DeleteTenantModal
          tenant={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={(text) => handleDelete(deleteTarget, text)}
        />
      )}

      <Drawer isOpen={!!drawer} onClose={() => setDrawer(null)} title={drawer?.name}>
        {drawer && (
          <>
            <Tabs
              items={[
                { id: 'overview', label: 'סקירה' },
                { id: 'users', label: 'משתמשים' },
                { id: 'billing', label: 'חיוב' },
                { id: 'logs', label: 'לוגים' },
              ]}
              value={tab}
              onChange={(id) => setTab(id as never)}
            />
            <div style={{ padding: 'var(--pad-card)' }}>
              {tab === 'overview' && (
                <TenantOverviewTab tenant={drawerFull ?? drawer} loading={drawerLoading} />
              )}
              {tab === 'users' && <TenantUsersTab tenantId={drawer.id} />}
              {tab === 'billing' && <TenantBillingTab tenantId={drawer.id} />}
              {tab === 'logs' && <TenantLogsTab tenantId={drawer.id} />}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}

function DeleteTenantModal({ tenant, onClose, onConfirm }: {
  tenant: TenantRow;
  onClose: () => void;
  onConfirm: (confirmText: string) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const match = text.trim() === tenant.slug;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="מחיקת לקוח לצמיתות"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>ביטול</button>
          <button
            type="button"
            className="btn danger"
            disabled={!match || busy}
            onClick={() => { setBusy(true); onConfirm(text.trim()); }}
          >
            {busy ? 'מוחק…' : 'מחק הכל לצמיתות'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 10 }}>
        <p style={{ margin: 0 }}>
          פעולה זו <strong>בלתי הפיכה</strong>. היא תמחק את <strong>{tenant.name}</strong> ואת כל הנתונים הקשורים אליו:
          משתמשים, היסטוריית שיחות, הקלטות, הודעות צ׳אט ו-SMS, הודעות תא קולי, אנשי קשר, קמפיינים ושלוחות.
        </p>
        <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 'var(--t-sm)' }}>
          טיקטים בתמיכה שנפתחו מהעסק <strong>יישמרו</strong>.
        </p>
        <FormField label={`להמשך, הקלד את מזהה הלקוח: ${tenant.slug}`}>
          <TextInput value={text} onChange={(e) => setText(e.target.value)} dir="ltr" placeholder={tenant.slug} />
        </FormField>
      </div>
    </Modal>
  );
}

function TenantForm({
  mode,
  tenant,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  tenant?: TenantRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: tenant?.name ?? '',
    slug: tenant?.slug ?? '',
    max_agents: tenant?.max_agents ?? 10,
    locale: tenant?.locale ?? 'he',
    timezone: tenant?.timezone ?? 'Asia/Jerusalem',
    sip_domain: tenant?.sip_domain ?? '',
    status: (tenant?.status as 'active' | 'trial' | 'suspended') ?? 'trial',
    trial_ends_at: tenant?.trial_ends_at?.slice(0, 10) ?? '',
    // Usage pricing (shekels in the UI, agorot on the wire).
    price_per_user: tenant?.price_per_user_cents != null ? String(tenant.price_per_user_cents / 100) : '',
    price_per_minute: tenant?.price_per_minute_cents != null ? String(tenant.price_per_minute_cents / 100) : '',
  });
  const [saving, setSaving] = useState(false);

  // מנוע תמלול-מילות-מפתח פר-מרכזיה + מפתח OpenRouter — נטען מה-GET המלא
  // בעריכה בלבד (ברשימת הלקוחות אין את המידע הזה). המפתח לעולם לא מוחזר
  // מהשרת; keyTouched קובע אם בכלל נשלח (מחרוזת ריקה = מחיקה).
  const [kw, setKw] = useState<{ engine: string; options: string[]; hasKey: boolean; fallback: boolean; loaded: boolean }>({
    engine: 'gemini', options: ['gemini'], hasKey: false, fallback: true, loaded: false,
  });
  const [keyReplace, setKeyReplace] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [emo, setEmo] = useState<{ enabled: boolean; engine: string; realtime: boolean; options: string[]; loaded: boolean }>({
    enabled: false, engine: 'gemini', realtime: false, options: ['gemini', 'gemini-audio'], loaded: false,
  });
  // AI לשיחות: תמלול+סיכום או תמלול בלבד + המודל. ביצירה אין מה לטעון
  // מהשרת — הברירות זמינות מיד; בעריכה נטען מה-GET המלא (כמו המנועים).
  type CaiOpt = { key: string; label: string; external: boolean };
  const CAI_BUILTIN: CaiOpt[] = ['gemini-3.5-flash', 'gemini-3.1-pro-preview', 'gemini-3.1-flash-lite', 'gemini-2.5-flash', 'gemini-2.5-pro']
    .map((k) => ({ key: k, label: k, external: false }));
  const [cai, setCai] = useState<{ mode: 'none' | 'transcript' | 'full'; transcribeModel: string; summaryModel: string; tOptions: CaiOpt[]; sOptions: CaiOpt[]; loaded: boolean }>({
    mode: 'full', transcribeModel: 'gemini-2.5-flash', summaryModel: 'gemini-2.5-flash',
    tOptions: CAI_BUILTIN, sOptions: CAI_BUILTIN,
    loaded: mode === 'create',
  });

  useEffect(() => {
    if (mode !== 'edit' || !tenant) return;
    void superApi.tenant(tenant.id).then((full) => {
      const ke = (full as { keyword_engine?: { engine: string; engine_options: string[]; has_openrouter_key: boolean; fallback_enabled?: boolean } })?.keyword_engine;
      if (ke) setKw({ engine: ke.engine, options: ke.engine_options, hasKey: ke.has_openrouter_key, fallback: ke.fallback_enabled !== false, loaded: true });
      const ea = (full as { emotion_analysis?: { enabled: boolean; engine: string; realtime: boolean; engine_options: string[] } })?.emotion_analysis;
      if (ea) setEmo({ enabled: ea.enabled, engine: ea.engine, realtime: ea.realtime, options: ea.engine_options, loaded: true });
      const ca = (full as { call_ai?: { mode?: 'none' | 'transcript' | 'full'; summary?: boolean; transcribe_model?: string; summary_model?: string; model?: string; transcribe_options?: CaiOpt[]; summary_options?: CaiOpt[] } })?.call_ai;
      // גם אם השרת (גרסה ישנה) לא החזיר call_ai — משחררים את השדות לעריכה
      // עם הברירות; אחרת הבוררים נשארים נעולים לנצח.
      if (ca) {
        setCai((prev) => ({
          mode: ca.mode ?? (ca.summary === false ? 'transcript' : 'full'),
          transcribeModel: ca.transcribe_model ?? ca.model ?? prev.transcribeModel,
          summaryModel: ca.summary_model ?? ca.model ?? prev.summaryModel,
          tOptions: ca.transcribe_options?.length ? ca.transcribe_options : prev.tOptions,
          sOptions: ca.summary_options?.length ? ca.summary_options : prev.sOptions,
          loaded: true,
        }));
      } else setCai((prev) => ({ ...prev, loaded: true }));
    }).catch(() => {});
  }, [mode, tenant]);

  async function save() {
    if (!form.name || (mode === 'create' && !form.slug)) {
      toast('יש למלא שם ומזהה', 'danger');
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        max_agents: Number(form.max_agents),
        locale: form.locale,
        timezone: form.timezone,
        sip_domain: form.sip_domain || null,
        status: form.status,
        trial_ends_at: form.trial_ends_at || null,
        price_per_user_cents: form.price_per_user !== '' ? Math.round(Number(form.price_per_user) * 100) : null,
        price_per_minute_cents: form.price_per_minute !== '' ? Math.round(Number(form.price_per_minute) * 100) : null,
      };
      if (mode === 'create') {
        payload.slug = form.slug;
        payload.call_ai_mode = cai.mode;
        payload.call_ai_transcribe_model = cai.transcribeModel;
        payload.call_ai_summary_model = cai.summaryModel;
        await superApi.createTenant(payload);
        toast('הלקוח נוצר', 'ok');
      } else if (tenant) {
        // מנוע/מפתח נשלחים רק אחרי שהערכים האמיתיים נטענו — אחרת שמירה
        // מוקדמת הייתה דורסת את המנוע חזרה ל-gemini.
        if (kw.loaded) { payload.keyword_engine = kw.engine; payload.keyword_fallback = kw.fallback; }
        if (emo.loaded) { payload.emotion_enabled = emo.enabled; payload.emotion_engine = emo.engine; payload.emotion_realtime = emo.realtime; }
        if (cai.loaded) { payload.call_ai_mode = cai.mode; payload.call_ai_transcribe_model = cai.transcribeModel; payload.call_ai_summary_model = cai.summaryModel; }
        if (keyTouched) payload.openrouter_api_key = keyReplace;
        await superApi.updateTenant(tenant.id, payload);
        toast('עודכן', 'ok');
      }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'לקוח חדש' : `עריכת ${tenant?.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            ביטול
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="שם" required>
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="מזהה (slug)" required hint={mode === 'edit' ? 'המזהה לא ניתן לשינוי לאחר היצירה' : 'אותיות באנגלית, ספרות ומקפים'}>
          <TextInput
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            dir="ltr"
            disabled={mode === 'edit'}
          />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="מכסת נציגים">
            <TextInput
              type="number"
              value={String(form.max_agents)}
              onChange={(e) => setForm({ ...form, max_agents: Number(e.target.value) })}
              dir="ltr"
            />
          </FormField>
          <FormField label="סטטוס">
            <select
              className="select"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'trial' | 'suspended' })}
            >
              <option value="trial">ניסיון</option>
              <option value="active">פעיל</option>
              <option value="suspended">מושעה</option>
            </select>
          </FormField>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="מחיר למשתמש (₪ לחודש)" hint="לפי משתמשים פעילים">
            <TextInput type="number" value={form.price_per_user} onChange={(e) => setForm({ ...form, price_per_user: e.target.value })} dir="ltr" placeholder="0" />
          </FormField>
          <FormField label="מחיר לדקת שיחה (₪)" hint="לפי דקות דיבור בפועל">
            <TextInput type="number" value={form.price_per_minute} onChange={(e) => setForm({ ...form, price_per_minute: e.target.value })} dir="ltr" placeholder="0" />
          </FormField>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="שפה">
            <select
              className="select"
              value={form.locale}
              onChange={(e) => setForm({ ...form, locale: e.target.value })}
            >
              <option value="he">עברית</option>
              <option value="en">English</option>
            </select>
          </FormField>
          <FormField label="אזור זמן">
            <TextInput value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} dir="ltr" />
          </FormField>
        </div>
        <FormField label="SIP Domain">
          <TextInput value={form.sip_domain} onChange={(e) => setForm({ ...form, sip_domain: e.target.value })} dir="ltr" placeholder="agent.kailai.tech" />
        </FormField>
        <FormField label="סוף תקופת ניסיון">
          <TextInput type="date" value={form.trial_ends_at} onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })} dir="ltr" />
        </FormField>

        {/* AI לשיחות — מצב (ללא/תמלול/מלא) + מודל תמלול + מודל סיכום. */}
        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>AI לשיחות (תמלול וסיכום)</div>
          <div className="stack">
            <FormField label="מצב" hint={
              cai.mode === 'full' ? 'כל שיחה מוקלטת מתומללת, מסוכמת ומקבלת מדדי AI. החיוב לפי מחירי ה-AI בתוכנית.'
                : cai.mode === 'transcript' ? 'תמלול בלבד — בלי סיכום ומדדי AI. מחויב רק מחיר התמלול שבתוכנית.'
                  : 'ללא AI לשיחות — אין תמלול, סיכום, מילות מפתח או רגשות. אפס חיוב AI.'
            }>
              <select
                className="select"
                value={cai.mode}
                disabled={!cai.loaded}
                onChange={(e) => setCai({ ...cai, mode: e.target.value as 'none' | 'transcript' | 'full' })}
              >
                <option value="full">תמלול + סיכום AI</option>
                <option value="transcript">תמלול בלבד</option>
                <option value="none">ללא</option>
              </select>
            </FormField>
            {cai.mode !== 'none' && (
              <FormField label="מודל תמלול" hint={
                cai.tOptions.find((o) => o.key === cai.transcribeModel)?.external
                  ? '⚠️ מודל חיצוני (OpenRouter) — ההקלטות נשלחות לספק שלישי. ללא הפרדת דוברים.'
                  : 'Gemini ישיר — כלול במפתח המערכתי, עם הפרדת דוברים.'
              }>
                <select className="select" value={cai.transcribeModel} disabled={!cai.loaded}
                  onChange={(e) => setCai({ ...cai, transcribeModel: e.target.value })}>
                  {cai.tOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}{o.external ? ' (חיצוני)' : ''}</option>
                  ))}
                </select>
              </FormField>
            )}
            {cai.mode === 'full' && (
              <FormField label="מודל סיכום" hint={
                cai.sOptions.find((o) => o.key === cai.summaryModel)?.external
                  ? '⚠️ מודל חיצוני (OpenRouter) — התמלולים נשלחים לספק שלישי.'
                  : 'Gemini ישיר — כלול במפתח המערכתי.'
              }>
                <select className="select" value={cai.summaryModel} disabled={!cai.loaded}
                  onChange={(e) => setCai({ ...cai, summaryModel: e.target.value })}>
                  {cai.sOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}{o.external ? ' (חיצוני)' : ''}</option>
                  ))}
                </select>
              </FormField>
            )}
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
              המודלים ברשימה מנוהלים במסך AI במערכת ← מודלי AI העובדים במערכת.
            </div>
          </div>
        </div>

        {/* מנוע תמלול למילות מפתח — נקבע כאן פר-מרכזיה (הוסר מהגדרות העסק). */}
        {mode === 'edit' && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>מנוע תמלול (התראות מילות מפתח)</div>
            <div className="stack">
              <FormField label="מנוע" hint={!kw.loaded ? 'טוען…' : kw.engine !== 'gemini' && !kw.hasKey && !(keyTouched && keyReplace.trim()) ? 'מנוע זה דורש מפתח OpenRouter — ללא מפתח של הלקוח ייעשה שימוש במפתח המערכתי.' : undefined}>
                <select
                  className="select"
                  value={kw.engine}
                  disabled={!kw.loaded}
                  onChange={(e) => setKw({ ...kw, engine: e.target.value })}
                >
                  {kw.options.map((o) => <option key={o} value={o}>{o === 'gemini' ? 'Gemini (ברירת מחדל — כלול)' : o}</option>)}
                </select>
              </FormField>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>גיבוי Gemini כשהמנוע נכשל</div>
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {kw.fallback ? 'דלוק — מנוע שנכשל נופל אוטומטית לתמלול ברירת המחדל (מומלץ).' : 'כבוי — מקטע שהמנוע נכשל עליו לא יתומלל כלל.'}
                  </div>
                </div>
                <Toggle checked={kw.fallback} onChange={(v: boolean) => setKw({ ...kw, fallback: v })} disabled={!kw.loaded} />
              </div>
              <FormField label="מפתח OpenRouter של הלקוח" hint={kw.hasKey && !keyTouched ? 'מפתח שמור ✓ — הזנת מפתח חדש תחליף אותו; שדה ריק ושמירה ימחקו אותו.' : 'משמש למנועי OpenRouter. השאר ריק לשימוש במפתח המערכתי.'}>
                <TextInput
                  type="password"
                  dir="ltr"
                  value={keyReplace}
                  onChange={(e) => { setKeyReplace(e.target.value); setKeyTouched(true); }}
                  placeholder={kw.hasKey ? '••••••••  (מפתח שמור)' : 'sk-or-...'}
                  autoComplete="new-password"
                />
              </FormField>
            </div>
          </div>
        )}

        {/* ניתוח רגשות לשיחות — מתג + מנוע + זמן אמת, פר-מרכזיה. */}
        {mode === 'edit' && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>ניתוח רגשות לשיחות</div>
            <div className="stack">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>הפעלת הפיצ'ר</div>
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>גרף רגשות, פרופיל אישיות ורגעי מפתח לכל שיחה מוקלטת.</div>
                </div>
                <Toggle checked={emo.enabled} onChange={(v: boolean) => setEmo({ ...emo, enabled: v })} disabled={!emo.loaded} />
              </div>
              {emo.enabled && (
                <>
                  <FormField label="מנוע" hint={emo.engine === 'gemini-audio' ? 'מנתח גם את טון הקול (יקר יותר).' : emo.engine.startsWith('hume') ? 'מנוע prosody חיצוני — דורש מפתח Hume מוגדר.' : 'ניתוח טקסטואלי מהתמליל (מהיר וזול).'}>
                    <select className="select" value={emo.engine} disabled={!emo.loaded} onChange={(e) => setEmo({ ...emo, engine: e.target.value })}>
                      {emo.options.map((o) => (
                        <option key={o} value={o}>
                          {o === 'gemini' ? 'Gemini (ברירת מחדל — כלול)' : o === 'gemini-audio' ? 'Gemini + טון קול' : o === 'hume-prosody' ? 'Hume — רגש מטון הקול (48 מימדים)' : o}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>ניתוח בזמן אמת</div>
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>מנטר רגש תוך כדי שיחה חיה ומתריע למפקח על לקוח מתוסכל.</div>
                    </div>
                    <Toggle checked={emo.realtime} onChange={(v: boolean) => setEmo({ ...emo, realtime: v })} disabled={!emo.loaded} />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function TenantOverviewTab({ tenant, loading }: { tenant: TenantRow; loading: boolean }) {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.set);

  async function impersonate() {
    try {
      const data = (await superApi.impersonateTenant(tenant.id)) as {
        token: string;
        persona: Persona;
        tenant_slug: string;
        user: AuthUser;
      };
      setAuth({
        token: data.token,
        user: data.user,
        persona: data.persona,
        tenant: { id: tenant.id, slug: data.tenant_slug, name: tenant.name, status: tenant.status },
      });
      toast(`נכנסת כ-${data.user.email}`, 'ok');
      navigate(data.persona === 'agent' ? '/agent' : '/admin');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בכניסה כלקוח';
      toast(msg, 'danger');
    }
  }

  if (loading) {
    return <div style={{ color: 'var(--muted)' }}>טוען…</div>;
  }

  return (
    <div className="stack">
      <Row k="מזהה (slug)" v={tenant.slug} mono />
      <Row k="שם" v={tenant.name} />
      <Row k="סטטוס" v={STATUS_LABEL[tenant.status] ?? tenant.status} />
      <Row k="נציגים" v={`${tenant.agent_count ?? 0} מתוך ${tenant.max_agents}`} />
      <Row k="שפה" v={tenant.locale === 'he' ? 'עברית' : 'English'} />
      {tenant.timezone && <Row k="אזור זמן" v={tenant.timezone} mono />}
      {tenant.currency && <Row k="מטבע" v={tenant.currency} mono />}
      {tenant.sip_domain && <Row k="SIP Domain" v={tenant.sip_domain} mono />}
      {tenant.trial_ends_at && <Row k="סוף ניסיון" v={new Date(tenant.trial_ends_at).toLocaleDateString('he-IL')} />}
      {tenant.created_at && <Row k="נוצר" v={new Date(tenant.created_at).toLocaleDateString('he-IL')} />}
      <div style={{ marginTop: 12 }}>
        <button type="button" className="btn primary" onClick={impersonate} disabled={tenant.status === 'suspended'}>
          התחבר כלקוח (impersonate) →
        </button>
      </div>
    </div>
  );
}

export function TenantUsersTab({ tenantId }: { tenantId: number }) {
  const [users, setUsers] = useState<TenantUser[] | null>(null);
  const [formTarget, setFormTarget] = useState<{ mode: 'create' } | { mode: 'edit'; user: TenantUser } | null>(null);
  const [pwTarget, setPwTarget] = useState<TenantUser | null>(null);
  const [delTarget, setDelTarget] = useState<TenantUser | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(() => {
    setUsers(null);
    void superApi
      .tenantUsers(tenantId)
      .then((u) => setUsers(u as TenantUser[]))
      .catch(() => setUsers([]));
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleReset2fa(u: TenantUser) {
    setBusyId(u.id);
    try {
      await superApi.resetTenantUser2fa(tenantId, u.id);
      toast('2FA אופס', 'ok');
      load();
    } catch {
      toast('שגיאה באיפוס 2FA', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  async function handleSuspend(u: TenantUser) {
    setBusyId(u.id);
    try {
      await superApi.suspendTenantUser(tenantId, u.id);
      toast('המשתמש הושעה', 'ok');
      load();
    } catch {
      toast('שגיאה בהשעיית המשתמש', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(u: TenantUser) {
    setBusyId(u.id);
    try {
      await superApi.deleteTenantUser(tenantId, u.id);
      toast('המשתמש נמחק לצמיתות', 'ok');
      setDelTarget(null);
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה במחיקת המשתמש', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>
          {users === null ? 'טוען…' : `${users.length} משתמשים`}
        </p>
        <button type="button" className="btn primary" onClick={() => setFormTarget({ mode: 'create' })}>
          + משתמש חדש
        </button>
      </div>

      {users !== null && users.length === 0 && (
        <div style={{ color: 'var(--muted)' }}>אין משתמשים</div>
      )}

      {users !== null && users.length > 0 && (
        <div style={{ display: 'grid', gap: 6 }}>
          {users.map((u) => (
            <div
              key={u.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 8,
                padding: 'var(--pad-card)',
                background: 'var(--surface-2)',
                borderRadius: 'var(--r-2)',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>
                  {u.name}
                  {u.two_factor_enabled ? ' 🔒' : ''}
                </div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">
                  {u.email}
                  {u.extension ? ` • ext ${u.extension}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <StatusBadge tone={u.status === 'active' ? 'ok' : 'neutral'} size="sm" dot>
                  {roleLabel(u.role)}
                </StatusBadge>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn ghost" disabled={busyId === u.id} onClick={() => setFormTarget({ mode: 'edit', user: u })}>
                    ערוך
                  </button>
                  <button type="button" className="btn ghost" disabled={busyId === u.id} onClick={() => setPwTarget(u)}>
                    סיסמה
                  </button>
                  {u.two_factor_enabled && (
                    <button type="button" className="btn ghost" disabled={busyId === u.id} onClick={() => handleReset2fa(u)}>
                      אפס 2FA
                    </button>
                  )}
                  {u.status !== 'suspended' && (
                    <button type="button" className="btn danger" disabled={busyId === u.id} onClick={() => handleSuspend(u)}>
                      השעה
                    </button>
                  )}
                  <button type="button" className="btn danger" disabled={busyId === u.id} onClick={() => setDelTarget(u)}>
                    מחק
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {formTarget && (
        <UserForm
          tenantId={tenantId}
          mode={formTarget.mode}
          user={formTarget.mode === 'edit' ? formTarget.user : undefined}
          onClose={() => setFormTarget(null)}
          onSaved={() => {
            setFormTarget(null);
            load();
          }}
        />
      )}

      {pwTarget && (
        <PasswordModal
          tenantId={tenantId}
          user={pwTarget}
          onClose={() => setPwTarget(null)}
          onSaved={() => setPwTarget(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!delTarget}
        onClose={() => setDelTarget(null)}
        onConfirm={() => delTarget && void handleDelete(delTarget)}
        title="מחיקת משתמש לצמיתות"
        message={delTarget ? `למחוק את ${delTarget.name} (${delTarget.email})? הפעולה אינה הפיכה והמשתמש יוסר לחלוטין.` : ''}
        confirmLabel="מחק לצמיתות"
        tone="danger"
      />
    </div>
  );
}

function UserForm({
  tenantId,
  mode,
  user,
  onClose,
  onSaved,
}: {
  tenantId: number;
  mode: 'create' | 'edit';
  user?: TenantUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    role: user?.role ?? 'agent',
    extension: user?.extension ?? '',
    status: (user?.status as 'active' | 'suspended') ?? 'active',
    password: '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name || !form.email || (mode === 'create' && !form.password)) {
      toast('יש למלא שם, אימייל' + (mode === 'create' ? ' וסיסמה' : ''), 'danger');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        await superApi.createTenantUser(tenantId, {
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
        });
        toast('המשתמש נוצר', 'ok');
      } else if (user) {
        await superApi.updateTenantUser(tenantId, user.id, {
          name: form.name,
          email: form.email,
          role: form.role,
          phone: form.phone || null,
          extension: form.extension || null,
          status: form.status,
        });
        toast('עודכן', 'ok');
      }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'משתמש חדש' : `עריכת ${user?.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            ביטול
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="שם" required>
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="אימייל" required>
          <TextInput value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} dir="ltr" />
        </FormField>
        {mode === 'create' && (
          <FormField label="סיסמה" required hint="12+ תווים, אותיות גדולות וקטנות, מספר וסמל">
            <TextInput
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              dir="ltr"
            />
          </FormField>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="תפקיד">
            <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {USER_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.he}
                </option>
              ))}
            </select>
          </FormField>
          {mode === 'edit' && (
            <FormField label="סטטוס">
              <select
                className="select"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'suspended' })}
              >
                <option value="active">פעיל</option>
                <option value="suspended">מושעה</option>
              </select>
            </FormField>
          )}
        </div>
        {mode === 'edit' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
            <FormField label="טלפון">
              <TextInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} dir="ltr" />
            </FormField>
            <FormField label="שלוחה">
              <TextInput value={form.extension} onChange={(e) => setForm({ ...form, extension: e.target.value })} dir="ltr" />
            </FormField>
          </div>
        )}
      </div>
    </Modal>
  );
}

function PasswordModal({
  tenantId,
  user,
  onClose,
  onSaved,
}: {
  tenantId: number;
  user: TenantUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!password) {
      toast('יש להזין סיסמה', 'danger');
      return;
    }
    setSaving(true);
    try {
      await superApi.resetTenantUserPassword(tenantId, user.id, password);
      toast('הסיסמה אופסה', 'ok');
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה באיפוס הסיסמה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`איפוס סיסמה — ${user.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            ביטול
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'מאפס…' : 'אפס סיסמה'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="סיסמה חדשה" required hint="12+ תווים, אותיות גדולות וקטנות, מספר וסמל">
          <TextInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" />
        </FormField>
      </div>
    </Modal>
  );
}

function TenantBillingTab({ tenantId }: { tenantId: number }) {
  const [billing, setBilling] = useState<BillingPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void superApi
      .tenantBilling(tenantId)
      .then((b) => setBilling(b as BillingPayload))
      .catch(() => setBilling(null))
      .finally(() => setLoading(false));
  }, [tenantId]);

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;
  if (!billing) return <div style={{ color: 'var(--muted)' }}>אין מידע</div>;

  const fmtMoney = (cents: number, currency: string) =>
    new Intl.NumberFormat('he-IL', { style: 'currency', currency }).format(cents / 100);

  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        <Stat label="מנוי פעיל" value={billing.plan?.name ?? '—'} />
        <Stat label="חשבוניות בטיפול" value={String(billing.stats.outstanding_invoices)} />
      </div>

      {billing.plan && (
        <div style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>תוכנית</div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>{billing.plan.name}</div>
          <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginTop: 4 }}>
            {fmtMoney(billing.plan.monthly_price_cents, billing.plan.currency)} / חודש •{' '}
            {billing.plan.minutes_included.toLocaleString('he-IL')} דק׳ • עד {billing.plan.max_agents} נציגים
          </div>
        </div>
      )}

      <div>
        <h4 style={{ margin: '8px 0' }}>חשבוניות אחרונות</h4>
        {billing.invoices.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין חשבוניות</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {billing.invoices.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: 8,
                  padding: 'var(--pad-card)',
                  background: 'var(--surface-2)',
                  borderRadius: 'var(--r-2)',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }} dir="ltr">
                    {inv.number ?? `#${inv.id}`}
                  </div>
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {inv.period ?? (inv.due_at ? new Date(inv.due_at).toLocaleDateString('he-IL') : '')}
                  </div>
                </div>
                <div style={{ fontWeight: 600 }}>{fmtMoney(inv.amount_cents, inv.currency)}</div>
                <StatusBadge
                  tone={inv.status === 'paid' ? 'ok' : inv.status === 'overdue' ? 'danger' : 'warn'}
                  size="sm"
                  dot
                >
                  {inv.status}
                </StatusBadge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TenantLogsTab({ tenantId }: { tenantId: number }) {
  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: 'var(--t-sm)' }}>
        אירועי audit אחרונים של הלקוח.
      </p>
      <Link to={`/super/audit?tenant_id=${tenantId}`} className="btn">
        פתח לוג מלא →
      </Link>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <span dir={mono ? 'ltr' : undefined} style={{ fontFamily: mono ? 'var(--font-mono)' : undefined }}>
        {v}
      </span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
      <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{label}</div>
      <div style={{ fontSize: 'var(--t-2xl)', fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}
